/**
 * R7.2.2C1 — Canonical Customer Mini App navigation contract.
 * Internal resources use the SPA stack only — never t.me / absolute alias reload.
 */

export type CustomerNavKind = "ROUTER_INTERNAL" | "TELEGRAM_NATIVE" | "EXTERNAL";

export type CustomerRouteName =
  | "home"
  | "profile"
  | "profile-edit"
  | "delivery-addresses"
  | "buyer-profiles"
  | "orders"
  | "order-detail"
  | "messages"
  | "invite-friends"
  | "documents-home"
  | "product"
  | "cart"
  | "checkout"
  | "search"
  | "category"
  | "promotions"
  | "engagement-hub"
  | "success"
  | "new"
  | "brands"
  | "brand"
  | "countries"
  | "country"
  | "bonuses"
  | "dropshipping"
  | "ime-debug";

export type CustomerRouteManifestEntry = {
  route: CustomerRouteName;
  component: string;
  logicalParent: CustomerRouteName | null;
  navigationKind: CustomerNavKind;
  directEntryAllowed: boolean;
  /** Customer-visible identity for order routes */
  orderIdContract?: "public_or_internal_resolved" | "none";
};

/** Canonical route map — build fails if critical routes disappear (guard test). */
export const CUSTOMER_ROUTE_MANIFEST: readonly CustomerRouteManifestEntry[] = [
  { route: "home", component: "HomeV2", logicalParent: null, navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "profile", component: "ProfileV2/about-v2", logicalParent: "home", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "profile-edit", component: "ProfileV2", logicalParent: "profile", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: false },
  { route: "delivery-addresses", component: "ProfileV2", logicalParent: "profile", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: false },
  { route: "buyer-profiles", component: "ProfileV2", logicalParent: "profile", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: false },
  {
    route: "orders",
    component: "MyOrdersV2",
    logicalParent: "profile",
    navigationKind: "ROUTER_INTERNAL",
    directEntryAllowed: true,
  },
  {
    route: "order-detail",
    component: "OrderDetailV2",
    logicalParent: "orders",
    navigationKind: "ROUTER_INTERNAL",
    directEntryAllowed: true,
    orderIdContract: "public_or_internal_resolved",
  },
  {
    route: "messages",
    component: "MessagesV2",
    logicalParent: "profile",
    navigationKind: "ROUTER_INTERNAL",
    directEntryAllowed: true,
  },
  {
    route: "documents-home",
    component: "CustomerOrderDocumentsHomeV2",
    logicalParent: "profile",
    navigationKind: "ROUTER_INTERNAL",
    directEntryAllowed: false,
  },
  {
    route: "invite-friends",
    component: "InviteFriendsV2",
    logicalParent: "profile",
    navigationKind: "ROUTER_INTERNAL",
    directEntryAllowed: true,
  },
  { route: "product", component: "ProductV2", logicalParent: "home", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "cart", component: "CartV2View", logicalParent: "home", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "checkout", component: "CheckoutV2View", logicalParent: "cart", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: false },
  { route: "search", component: "SearchV2", logicalParent: "home", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "category", component: "CatalogListV2", logicalParent: "home", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "promotions", component: "PromotionsV2", logicalParent: "home", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "engagement-hub", component: "EngagementHubPlaceholderV2", logicalParent: "profile", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: false },
  { route: "success", component: "OrderPaymentPanelV2", logicalParent: "home", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "new", component: "NewArrivals", logicalParent: "home", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "brands", component: "BrandsIndex", logicalParent: "home", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "brand", component: "BrandShelf", logicalParent: "brands", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "countries", component: "CountriesIndex", logicalParent: "home", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "country", component: "CountryShelf", logicalParent: "countries", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: true },
  { route: "bonuses", component: "BonusesInline", logicalParent: "profile", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: false },
  { route: "dropshipping", component: "DropshippingInline", logicalParent: "home", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: false },
  { route: "ime-debug", component: "ImeForensicDebugV2", logicalParent: "home", navigationKind: "ROUTER_INTERNAL", directEntryAllowed: false },
] as const;

export const CRITICAL_ACCOUNT_ROUTES: readonly CustomerRouteName[] = [
  "profile",
  "orders",
  "order-detail",
  "messages",
  "invite-friends",
  "documents-home",
] as const;

const BY_NAME = new Map(CUSTOMER_ROUTE_MANIFEST.map((e) => [e.route, e]));

export function getRouteManifestEntry(route: string): CustomerRouteManifestEntry | undefined {
  return BY_NAME.get(route as CustomerRouteName);
}

export function logicalParentOf(route: string): CustomerRouteName | null {
  return getRouteManifestEntry(route)?.logicalParent ?? null;
}

/** Public STG-… or internal ord_… — never invent URLs. */
export function isPublicOrderRef(ref: string): boolean {
  return /^STG-\d+/i.test(ref.trim());
}

export function isInternalOrderId(ref: string): boolean {
  return /^ord_/i.test(ref.trim());
}

export function normalizeCustomerOrderRef(ref: string): string {
  return String(ref ?? "").trim();
}

export type CustomerStackView = { name: string; orderId?: string; productId?: string; [k: string]: unknown };

/**
 * Build a safe stack that always includes home under account routes,
 * and inserts logical parents for direct-entry safety.
 */
export function stackForRoute(top: CustomerStackView): CustomerStackView[] {
  const name = String(top.name || "home");
  if (name === "home") return [{ name: "home" }];
  const chain: CustomerStackView[] = [{ name: "home" }];
  const parents: string[] = [];
  let cursor: string | null = logicalParentOf(name);
  const guard = new Set<string>();
  while (cursor && !guard.has(cursor)) {
    guard.add(cursor);
    parents.push(cursor);
    cursor = logicalParentOf(cursor);
  }
  for (const p of parents.reverse()) {
    if (p === "home") continue;
    if (p === "documents-home") continue; // overlay on profile, not a stack view
    chain.push({ name: p });
  }
  chain.push(top);
  return chain;
}

export type NavTelemetryDetail = {
  from?: string;
  to: string;
  via: "service" | "back" | "logical_parent_fallback" | "chrome" | "deep_link";
};

export type CustomerNavigationHandlers = {
  setStack: (next: CustomerStackView[] | ((prev: CustomerStackView[]) => CustomerStackView[])) => void;
  emit?: (name: string, detail: Record<string, unknown>) => void;
};

/**
 * Single navigation façade used by Profile tiles, documents, messages, deep links.
 */
export function createCustomerNavigationService(handlers: CustomerNavigationHandlers) {
  const emit = (event: string, detail: Record<string, unknown>) => {
    try {
      handlers.emit?.(event, detail);
    } catch {
      /* never break nav */
    }
  };

  const go = (top: CustomerStackView, via: NavTelemetryDetail["via"] = "service") => {
    const next = stackForRoute(top);
    handlers.setStack(next);
    emit("customer_internal_navigation", {
      to: top.name,
      via,
      stack_depth: next.length,
    });
    emit("customer_route_open", { route: top.name, via });
  };

  return {
    goHome: () => {
      handlers.setStack([{ name: "home" }]);
      emit("customer_internal_navigation", { to: "home", via: "service", stack_depth: 1 });
    },
    goProfile: () => go({ name: "profile" }),
    goOrders: () => go({ name: "orders" }),
    goOrder: (orderRef: string) => {
      const id = normalizeCustomerOrderRef(orderRef);
      go({ name: "order-detail", orderId: id });
    },
    goMessages: () => go({ name: "messages" }),
    goInviteFriends: () => go({ name: "invite-friends" }),
    goProduct: (productRef: string) => go({ name: "product", productId: String(productRef) }),
    goDocumentsHome: () => go({ name: "profile" }), // documents overlay lives on profile
    /** Prefer logical parent over blind history.back / WebApp.close. */
    goLogicalParent: (current: string) => {
      const parent = logicalParentOf(current);
      if (!parent || parent === "home") {
        handlers.setStack([{ name: "home" }]);
        emit("logical_parent_fallback", { from: current, to: "home" });
        emit("telegram_back_used", { from: current, to: "home", mode: "logical_parent" });
        return;
      }
      if (parent === "documents-home") {
        go({ name: "profile" }, "logical_parent_fallback");
        emit("telegram_back_used", { from: current, to: "profile", mode: "logical_parent" });
        return;
      }
      go({ name: parent }, "logical_parent_fallback");
      emit("telegram_back_used", { from: current, to: parent, mode: "logical_parent" });
    },
    replaceStack: (next: CustomerStackView[]) => handlers.setStack(next),
  };
}

export type CustomerNavigationService = ReturnType<typeof createCustomerNavigationService>;
